<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useConfirmOTPMutation } from '@/entities/auth'
import { useCustomToast } from '@/shared/lib'
import CButton from './CButton.vue'
import CDialog from './CDialog.vue'

interface Props {
  modelValue?: boolean
  phoneNumber?: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  phoneNumber: '',
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'verifyCode': [code: string, message: string]
  'goBack': []
}>()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const { t } = useI18n()
const { error: showError } = useCustomToast()

// OTP mutation for confirming
const { mutate: confirmOTP, isPending: isConfirmingOTP }
  = useConfirmOTPMutation()

const smsCode = ref('')

function closeDialog() {
  isOpen.value = false
  smsCode.value = ''
}

function goBack() {
  emit('goBack')
}

function handleConfirmCode() {
  if (smsCode.value && props.phoneNumber) {
    const number = props.phoneNumber.trim().replace(/^\+/, '')
    confirmOTP(
      {
        phone_number: number,
        otp: smsCode.value,
      },
      {
        onSuccess: (response) => {
          emit('verifyCode', smsCode.value, response?.data?.message)
          closeDialog()
        },
        onError: (error) => {
          console.error('Error confirming code:', error)
          showError({
            title: t('error'),
            message: t('invalid_confirmation_code'),
          })
        },
      },
    )
  }
  else {
    showError({
      title: t('error'),
      message: t('not_all_fields_filled'),
    })
  }
}
</script>

<template>
  <CDialog v-model="isOpen" class="max-w-[480px]" :title="t('enter_sms_code')">
    <template #header>
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-3">
          <button
            class="text-text-secondary hover:text-text-regular transition-colors"
            @click="goBack"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M15 6C15 6 9 10.419 9 12C9 13.581 15 18 15 18"
                stroke="#909399"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <h3 class="text-lg font-semibold text-primary">
            {{ $t("enter_sms_code") }}
          </h3>
        </div>
        <button
          class="text-text-secondary hover:text-text-regular transition-colors"
          @click="closeDialog"
        >
          <i class="icon-close text-xl" />
        </button>
      </div>
    </template>

    <div class="flex flex-col gap-4">
      <p class="text-sm text-primary leading-[22px] font-normal">
        {{ $t("enter_sms_code_description") }}
      </p>

      <div>
        <label class="text-sm text-text-secondary mb-2 block">
          {{ $t("enter_code") }}
        </label>
        <ElInput
          v-model="smsCode"
          :placeholder="t('enter')"
          size="large"
          class="w-full"
          maxlength="6"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-between gap-3">
        <CButton
          variant="ghost"
          class="!bg-white !border-border-light !text-text-secondary"
          @click="closeDialog"
        >
          {{ $t("cancel") }}
        </CButton>
        <CButton
          variant="primary"
          :loading="isConfirmingOTP"
          :disabled="isConfirmingOTP"
          @click="handleConfirmCode"
        >
          {{ $t("confirm") }}
        </CButton>
      </div>
    </template>
  </CDialog>
</template>
